import * as React from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import SortIcon from '@mui/icons-material/Sort';

export const CHART_SORT_OPTIONS = {
  NONE: 'Sort (None)',
  ASC: 'Sort (ASC)',
  DESC: 'Sort (DESC)',
};

const options = [CHART_SORT_OPTIONS.NONE, CHART_SORT_OPTIONS.ASC, CHART_SORT_OPTIONS.DESC];

const sortButtonStyles = {
  textTransform: 'none' as const,
  fontSize: '12px',
  fontWeight: 600,
  color: '#667085',
  borderColor: '#e5e7eb',
  backgroundColor: '#fff',
  px: 1.5,
  py: 0.5,
  '&:hover': {
    backgroundColor: '#f9fafb',
    borderColor: '#d0d5dd',
  },
};

const sortDropdownButtonStyles = {
  ...sortButtonStyles,
  px: 0.5,
  minWidth: 'auto',
};

const menuItemStyles = {
  fontSize: '13px',
  color: '#344054',
  '&.Mui-selected': {
    backgroundColor: '#ecfdf5',
    color: '#42BB93',
    '&:hover': {
      backgroundColor: '#d1fae5',
    },
  },
};

interface ButtonSortChartProps {
  onChange: (value: string) => void;
}

export function ButtonSortChart(props: ButtonSortChartProps) {
  const { onChange } = props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const handleClick = () => {
    handleToggle();
  };

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    onChange(options[index]);
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        ref={anchorRef}
        aria-label='Button group with a nested menu'
        variant='outlined'
        sx={{
          height: 32,
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderColor: '#e5e7eb',
          },
        }}
      >
        <Button sx={sortButtonStyles} onClick={handleClick} startIcon={<SortIcon sx={{ fontSize: '16px !important' }} />}>
          {options[selectedIndex]}
        </Button>
        <Button size='small' sx={sortDropdownButtonStyles} onClick={handleToggle}>
          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement='bottom-end'
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper
              sx={{
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                mt: 0.5,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem sx={{ py: 0.5 }}>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                      sx={menuItemStyles}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
}
